import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Button,
  Snackbar,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
  DragIndicator as DragIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { getPlatformColor, getPlatformName } from '../config/platforms';
import { shiftScheduleToDay, daysBetween } from '../utils/shiftSchedule';
import { statusMeta } from '../config/postStatus';
import { PostDetailDialog, DetailPost } from '../components/PostDetailDialog';
import { EditPostDialog } from '../components/EditPostDialog';
import { PostPreviewDialog } from '../components/PostPreviewDialog';
import { SchedulePostDialog } from '../components/SchedulePostDialog';
import { DeletePostDialog } from '../components/DeletePostDialog';
import { usePostActions } from '../components/usePostActions';

const API_URL = process.env.REACT_APP_API_URL || '';

interface CalendarPost extends DetailPost {
  created_at: string;
}

// Statuses that belong on a calendar: something is either due to go out or it went out.
// A draft has no date to place it on, so it is not shown.
const SHOWN_STATUSES = ['scheduled', 'published', 'partial', 'failed', 'rejected'];

/**
 * SQLite CURRENT_TIMESTAMP is UTC with no zone marker. Without the explicit Z the browser
 * reads it as local time, which silently shifts every post by the UTC offset — enough to
 * land a post on the wrong day in the grid.
 */
const parseStamp = (stamp?: string | null): Date | null => {
  if (!stamp) return null;
  const iso = stamp.includes('T') ? stamp : stamp.replace(' ', 'T') + 'Z';
  const date = new Date(iso);
  return isNaN(date.getTime()) ? null : date;
};

/**
 * The date a post belongs on: when it went out, or when it is due.
 *
 * created_at is the last resort. A post that failed or was rejected has no published_at and
 * may never have been scheduled, and those are the ones most worth seeing — falling back to
 * the day it was written keeps them on the calendar instead of silently dropping them.
 *
 * `next_due_at` comes first for a scheduled post because a post with per-network times has no
 * single date: its base time may already have passed for the networks that have published,
 * and a calendar answers "what happens next", so the next outstanding window is the honest
 * placement. A post with one shared time has next_due_at equal to scheduled_for, so nothing
 * moves for the ordinary case.
 */
const dateFor = (post: CalendarPost): Date | null =>
  parseStamp(
    post.status === 'scheduled'
      ? post.next_due_at || post.scheduled_for
      : post.published_at || post.scheduled_for || post.created_at
  );

/**
 * One row in a day cell.
 *
 * `platform` is set only when a staggered post has been split, so the row can name the network
 * it represents. `null` means the whole post — either it publishes everywhere at once, or it
 * has already gone out.
 */
interface CalendarEntry {
  post: CalendarPost;
  platform: string | null;
  when: Date | null;
}

/** Networks on this post that publish at different moments. */
const isStaggered = (post: CalendarPost): boolean =>
  new Set(post.platforms.map(p => post.schedule_variants?.[p] || post.scheduled_for)).size > 1;

const sameDay = (a: Date, b: Date) =>
  a.getDate() === b.getDate() && a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();

const timeOf = (date: Date) =>
  date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

export const Calendar: React.FC = () => {
  const { token } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [posts, setPosts] = useState<CalendarPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [targets, setTargets] = useState<Record<string, string | null>>({});

  /**
   * Drag-and-drop rescheduling.
   *
   * `dragging` is the post under the cursor; `dragOverDay` is the cell it would land on, kept
   * so the target can be highlighted — without it there is no feedback about where a drop
   * will go, which on a month grid is most of the usability.
   *
   * Native HTML5 drag rather than a library: a month grid is a few dozen drop targets and the
   * whole interaction is dragstart/dragover/drop. react-dnd would be ~30 KB for that.
   */
  const [dragging, setDragging] = useState<CalendarPost | null>(null);
  const [dragOverDay, setDragOverDay] = useState<string | null>(null);
  /**
   * A whole day being dragged by its handle, rather than one post.
   *
   * Holds every scheduled post on that day and the day itself, so the offset can be worked
   * out from the day rather than from whichever post happened to be grabbed. Moving four
   * staggered posts one at a time is four drags and four round trips; this is one gesture.
   */
  const [draggingDay, setDraggingDay] = useState<{ key: string; date: Date; posts: CalendarPost[] } | null>(null);
  /** The day currently being moved, so its cell can show progress. */
  const [movingDay, setMovingDay] = useState<string | null>(null);
  const [dayMoveError, setDayMoveError] = useState('');

  const [selected, setSelected] = useState<CalendarPost | null>(null);
  const [editing, setEditing] = useState<CalendarPost | null>(null);
  const [previewing, setPreviewing] = useState<CalendarPost | null>(null);
  const [scheduling, setScheduling] = useState<CalendarPost | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<CalendarPost | null>(null);

  // The month being viewed, so the fetch can be bounded rather than pulling all history
  const monthStart = useMemo(
    () => new Date(currentDate.getFullYear(), currentDate.getMonth(), 1),
    [currentDate]
  );
  const monthEnd = useMemo(
    () => new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0, 23, 59, 59),
    [currentDate]
  );

  const loadPosts = useCallback(async () => {
    setLoading(true);
    try {
      // Every shown status in one request (it used to be one per status). Capped: a month's
      // worth of posts is small, and this avoids fetching every post ever made.
      const res = await fetch(`${API_URL}/api/posts?status=${SHOWN_STATUSES.join(',')}&limit=500`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPosts(res.ok ? await res.json() : []);
    } catch {
      setError('Could not load posts for the calendar');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const actions = usePostActions(loadPosts);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  useEffect(() => {
    fetch(`${API_URL}/api/platforms`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (!data) return;
        setTargets(
          Object.fromEntries(
            data.platforms.map((p: { platform: string; target: string | null }) => [p.platform, p.target])
          )
        );
      })
      .catch(() => {
        /* preview falls back to the platform name */
      });
  }, [token]);

  // Keep an open dialog in step after an action changes the post
  useEffect(() => {
    if (!selected) return;
    const fresh = posts.find(p => p.id === selected.id);
    if (fresh && fresh !== selected) setSelected(fresh);
  }, [posts, selected]);

  // Bucket by day once per data change rather than filtering the whole list per cell,
  // which was 31 passes over every post on every render.
  //
  // A staggered post contributes one entry per network rather than one for the post. It
  // previously appeared once, at its earliest time, with the other networks hidden behind a
  // tooltip — so a post going out at 12:30, 1:15, 2:30 and 3:00 looked like a single 12:30
  // post, and setting per-network times appeared to do nothing here.
  //
  // The entry keeps a reference to the whole post, so clicking opens the same detail dialog
  // and dragging still moves every network together.
  const byDay = useMemo(() => {
    const map = new Map<string, CalendarEntry[]>();

    const add = (date: Date | null, entry: CalendarEntry) => {
      if (!date) return;
      const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
      const list = map.get(key) || [];
      list.push(entry);
      map.set(key, list);
    };

    posts.forEach(post => {
      if (post.status === 'scheduled' && isStaggered(post)) {
        // One row per network, each on the day and at the time that network actually publishes.
        // A platform without its own variant falls back to the base time, as the scheduler does.
        post.platforms.forEach(platform => {
          const iso = post.schedule_variants?.[platform] || post.scheduled_for;
          const when = parseStamp(iso);
          add(when, { post, platform, when });
        });
        return;
      }
      add(dateFor(post), { post, platform: null, when: dateFor(post) });
    });

    // Chronological within a day, so the column reads top to bottom as the day unfolds
    map.forEach(list =>
      list.sort((a, b) => (a.when?.getTime() || 0) - (b.when?.getTime() || 0))
    );
    return map;
  }, [posts]);

  const postsOn = (date: Date) =>
    byDay.get(`${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`) || [];

  const monthCount = useMemo(
    () =>
      posts.filter(p => {
        const d = dateFor(p);
        return d && d >= monthStart && d <= monthEnd;
      }).length,
    [posts, monthStart, monthEnd]
  );

  const isToday = (date: Date) => sameDay(date, new Date());

  const renderCells = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDay = new Date(year, month, 1).getDay();
    const cells: React.ReactElement[] = [];

    ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].forEach(day => {
      cells.push(
        <Box
          key={`header-${day}`}
          sx={{
            p: 1,
            textAlign: 'center',
            fontWeight: 600,
            color: 'text.secondary',
            fontSize: '0.8rem',
          }}
        >
          {day}
        </Box>
      );
    });

    for (let i = 0; i < firstDay; i++) {
      cells.push(<Box key={`empty-${i}`} />);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dayPosts = postsOn(date);
      const today = isToday(date);
      const dayKey = `${year}-${month}-${day}`;
      // Distinct posts, not rows: a staggered post contributes four entries to the cell but
      // must be moved once, or each of its networks would be shifted four times over.
      const movableToday = Array.from(
        new Map(
          dayPosts
            .filter(e => e.post.status === 'scheduled')
            .map(e => [e.post.id, e.post]),
        ).values(),
      );
      // Not a drop target for the day the post already sits on — a drop there is a no-op and
      // highlighting it would promise a change that will not happen.
      // Not a drop target for the day the dragged thing already sits on — a drop there is a
      // no-op, and highlighting it would promise a change that will not happen.
      const isDropTarget =
        dragOverDay === dayKey &&
        ((!!dragging && !sameDay(new Date(dragging.scheduled_for || ''), date)) ||
          (!!draggingDay && draggingDay.key !== dayKey));

      cells.push(
        <Card
          key={day}
          variant="outlined"
          // preventDefault on dragOver is what marks a element as a valid drop target;
          // without it the browser refuses the drop and nothing happens.
          onDragOver={(e) => {
            if (!dragging && !draggingDay) return;
            e.preventDefault();
            if (dragOverDay !== dayKey) setDragOverDay(dayKey);
          }}
          onDragLeave={() => { if (dragOverDay === dayKey) setDragOverDay(null); }}
          onDrop={(e) => { e.preventDefault(); handleDrop(date); }}
          sx={{
            minHeight: 120,
            // The drop target is highlighted rather than the dragged item: on a month grid the
            // question is "where will this land", not "what am I holding".
            borderColor: isDropTarget ? 'primary.main' : today ? 'primary.main' : 'divider',
            borderWidth: isDropTarget || today ? 2 : 1,
            bgcolor: isDropTarget
              ? 'primary.lighter'
              : today ? 'action.hover' : 'background.paper',
            outline: isDropTarget ? '2px dashed' : 'none',
            outlineColor: 'primary.main',
            transition: 'background-color 0.12s',
          }}
        >
          <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: today ? 700 : 500,
                  color: today ? 'primary.main' : 'text.primary',
                }}
              >
                {day}
              </Typography>

              {/* Move the whole day at once.
                  Only when there is more than one post to move — on a single post the handle
                  would duplicate what dragging the post already does, and a control that
                  does nothing new is clutter. */}
              {movableToday.length > 1 && (
                <Tooltip
                  arrow
                  title={`Move all ${movableToday.length} posts on this day — each keeps its own time`}
                >
                  <Box
                    draggable
                    onDragStart={(e) => {
                      setDraggingDay({ key: dayKey, date, posts: movableToday });
                      e.dataTransfer.effectAllowed = 'move';
                      // Firefox will not start a drag unless some data is set.
                      e.dataTransfer.setData('text/plain', dayKey);
                    }}
                    onDragEnd={() => { setDraggingDay(null); setDragOverDay(null); }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'grab',
                      '&:active': { cursor: 'grabbing' },
                      color: 'text.disabled',
                      opacity: draggingDay?.key === dayKey ? 0.4 : 1,
                      '&:hover': { color: 'text.secondary' },
                      '& svg': { fontSize: 15 },
                    }}
                  >
                    <DragIcon />
                  </Box>
                </Tooltip>
              )}

              {movingDay === dayKey && <CircularProgress size={12} />}
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {dayPosts.map((entry, entryIndex) => {
                const { post, platform, when } = entry;
                const meta = statusMeta(post.status);
                // A split row names only its own network; an unsplit one names them all.
                const rowPlatforms = platform ? [platform] : post.platforms;

                return (
                  <Tooltip
                    // A staggered post appears once per network, so the id alone is not unique.
                    key={`${post.id}-${platform || 'all'}-${entryIndex}`}
                    arrow
                    title={
                      <Box>
                        <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                          {when ? timeOf(when) : ''} · {meta.label}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                          {rowPlatforms.map(getPlatformName).join(', ')}
                        </Typography>
                        {/* A split row is one network of several, so say what the rest are
                            doing rather than leaving the row looking like the whole post. */}
                        {platform && (
                          <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontStyle: 'italic' }}>
                            One of {post.platforms.length} networks on this post, each at its own
                            time. Moving any of them moves all of them.
                          </Typography>
                        )}
                        <Typography variant="caption" sx={{ display: 'block' }}>
                          {post.content.slice(0, 120)}
                          {post.content.length > 120 ? '…' : ''}
                        </Typography>
                      </Box>
                    }
                  >
                    <Box
                      // Only a scheduled post can move. A published one has already gone out,
                      // and dragging it would promise something the app cannot do.
                      draggable={post.status === 'scheduled'}
                      onDragStart={(e) => {
                        setDragging(post);
                        e.dataTransfer.effectAllowed = 'move';
                        // Firefox refuses to start a drag unless some data is set.
                        e.dataTransfer.setData('text/plain', post.id);
                      }}
                      onDragEnd={() => { setDragging(null); setDragOverDay(null); }}
                      onClick={() => setSelected(post)}
                      sx={{
                        p: 0.5,
                        borderRadius: 1,
                        cursor: post.status === 'scheduled' ? 'grab' : 'pointer',
                        '&:active': post.status === 'scheduled' ? { cursor: 'grabbing' } : {},
                        borderLeft: 3,
                        borderLeftColor:
                          meta.color === 'default' ? 'divider' : `${meta.color}.main`,
                        bgcolor: 'action.hover',
                        // The item being dragged fades, so the highlighted target reads as
                        // where it is going rather than where it came from.
                        opacity: dragging?.id === post.id ? 0.4 : 1,
                        '&:hover': { bgcolor: 'action.selected' },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            color: meta.color === 'default' ? 'text.disabled' : `${meta.color}.main`,
                            '& svg': { fontSize: 13 },
                          }}
                        >
                          {meta.icon}
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{ fontSize: '0.68rem', fontWeight: 500, lineHeight: 1.2 }}
                        >
                          {when ? timeOf(when) : ''}
                        </Typography>
                      </Box>
                      {/* Platform dots rather than names: a cell is too narrow for labels,
                          and the tooltip spells them out. A split row shows only its own
                          network — showing all four on each of four rows would suggest every
                          network goes out at every time. */}
                      <Box sx={{ display: 'flex', gap: 0.25, flexWrap: 'wrap', mt: 0.25 }}>
                        {rowPlatforms.map(p2 => (
                          <Box
                            key={p2}
                            sx={{
                              width: 7,
                              height: 7,
                              borderRadius: '50%',
                              bgcolor: getPlatformColor(p2),
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          </CardContent>
        </Card>
      );
    }

    return cells;
  };

  /**
   * Move a dragged post to the day it was dropped on.
   *
   * Every platform keeps its own hour; only the date changes. Reuses actions.schedule, the
   * same path the reschedule dialog takes, so validation, the past-time check and the activity
   * log all behave identically whether a time was dragged or typed.
   */
  const handleDrop = async (targetDay: Date) => {
    const post = dragging;
    const day = draggingDay;
    setDragging(null);
    setDraggingDay(null);
    setDragOverDay(null);

    if (day) {
      // Every post on the day moves by the same offset, taken from the day rather than from
      // any one post — so posts that already sat at different times keep their spacing.
      const offset = daysBetween(day.date, targetDay);
      if (offset === 0) return;

      setDayMoveError('');
      setMovingDay(day.key);
      let moved = 0;
      const failed: string[] = [];

      // Sequential, not Promise.all: each call is a write the scheduler also reads, and four
      // at once against SQLite is a worse trade than four in a row that each take 40ms.
      for (const p of day.posts) {
        const shifted = shiftScheduleToDay(p, targetDay);
        if (!shifted) continue;
        const ok = await actions.schedule(p, shifted.scheduledFor, shifted.variants);
        if (ok) moved++;
        else failed.push(p.content.slice(0, 40));
      }

      setMovingDay(null);
      // A partial move is the dangerous outcome — some posts on the new day, some left
      // behind — so it is reported rather than left to be noticed.
      if (failed.length) {
        setDayMoveError(
          `Moved ${moved} of ${day.posts.length}. These stayed put: ${failed.join('; ')}.`
        );
      }
      return;
    }

    if (!post) return;

    // A published post cannot be moved, and a drop on its own day is a no-op.
    if (post.status !== 'scheduled') return;
    const shifted = shiftScheduleToDay(post, targetDay);
    if (!shifted) return;

    await actions.schedule(post, shifted.scheduledFor, shifted.variants);
  };

  return (
    <Box>
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" component="h1">Calendar</Typography>
          <Typography variant="body2" color="text.secondary">
            Scheduled posts on their due date, published posts on the day they went out.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button size="small" startIcon={<TodayIcon />} onClick={() => setCurrentDate(new Date())}>
            Today
          </Button>
          <IconButton
            size="small"
            onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1))}
          >
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="h6" sx={{ minWidth: { xs: 0, sm: 180 }, textAlign: 'center' }}>
            {currentDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1))}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {actions.error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={actions.clearMessages}>
          {actions.error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Card>
            <CardContent sx={{ p: { xs: 1, sm: 1.5 } }}>
              {/* Seven day columns do not fit a phone — at 390px each cell would be under
                  50px, narrower than the time text inside it. Scrolls inside the card so the
                  page body never scrolls sideways, the same treatment the posting schedule
                  matrix already uses for the same reason. */}
              <Box sx={{ overflowX: 'auto' }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    gap: 1,
                    minWidth: { xs: 660, sm: 0 },
                  }}
                >
                  {renderCells()}
                </Box>
              </Box>
            </CardContent>
          </Card>

          {monthCount === 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Nothing on the calendar for{' '}
              {currentDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}.
              Schedule a post from the composer, or from any draft in Post History.
            </Alert>
          )}
        </>
      )}

      {dayMoveError && (
        <Alert severity="warning" sx={{ mt: 2 }} onClose={() => setDayMoveError('')}>
          {dayMoveError}
        </Alert>
      )}

      {/* Drag-and-drop is invisible until tried, so say it once. */}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        Drag a scheduled post to another day to move it. Where a day has more than one, the
        handle beside the date moves them all at once. Every network keeps its own time — only
        the date changes.
      </Typography>

      {/* Legend: the icons in the cells are small, so name what each one means */}
      <Box sx={{ mt: 2, display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          {(['scheduled', 'published', 'partial', 'failed', 'rejected'] as const).map(status => {
            const meta = statusMeta(status);
            return (
              <Box key={status} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box
                  sx={{
                    display: 'flex',
                    color: meta.color === 'default' ? 'text.disabled' : `${meta.color}.main`,
                    '& svg': { fontSize: 15 },
                  }}
                >
                  {meta.icon}
                </Box>
                <Typography variant="caption" color="text.secondary">{meta.label}</Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      <PostDetailDialog
        post={selected}
        targets={targets}
        busy={actions.busyId === selected?.id}
        onClose={() => setSelected(null)}
        onPublish={p => actions.publish(p)}
        onSchedule={p => setScheduling(p as CalendarPost)}
        // Closed first so a refusal lands in the page's error banner, not behind the dialog
        onStage={p => { setSelected(null); actions.stage(p); }}
        // Closed first, like onStage, so the outcome shows on the page rather than behind the dialog
        onRetry={(p, platform, republish, mediaUrls) => { setSelected(null); actions.retry(p, platform, republish, mediaUrls); }}
        onEdit={p => setEditing(p as CalendarPost)}
        onDelete={p => setConfirmDelete(p as CalendarPost)}
        // Replaces the detail dialog rather than stacking on it, as Post History's does
        onPreview={p => { setSelected(null); setPreviewing(p as CalendarPost); }}
      />

      <PostPreviewDialog
        post={previewing}
        targets={targets}
        when={previewing?.status === 'scheduled' && previewing.scheduled_for
          ? new Date(previewing.scheduled_for).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
          : undefined}
        busy={actions.busyId === previewing?.id}
        onPublish={
          previewing && statusMeta(previewing.status).canPublish
            ? p => { setPreviewing(null); actions.publish(p as CalendarPost); }
            : undefined
        }
        onEdit={
          previewing && statusMeta(previewing.status).canEdit
            ? p => { setPreviewing(null); setEditing(p as CalendarPost); }
            : undefined
        }
        onClose={() => setPreviewing(null)}
      />

      {editing && (
        <EditPostDialog open post={editing} onClose={() => setEditing(null)} onSaved={loadPosts} />
      )}

      {scheduling && (
        <SchedulePostDialog
          open
          platforms={scheduling.platforms}
          postId={scheduling.id}
          initial={scheduling.scheduled_for}
          initialVariants={scheduling.schedule_variants}
          rescheduling={scheduling.status === 'scheduled'}
          busy={actions.busyId === scheduling.id}
          onClose={() => setScheduling(null)}
          onConfirm={async (iso, variants) => {
            const ok = await actions.schedule(scheduling, iso, variants);
            if (ok) setScheduling(null);
          }}
        />
      )}

      <DeletePostDialog
        post={confirmDelete}
        busy={actions.busyId === confirmDelete?.id}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={async (post, removeFromPlatforms) => {
          const ok = await actions.remove(post as CalendarPost, removeFromPlatforms);
          if (ok) {
            setConfirmDelete(null);
            setSelected(null);
          }
          return ok;
        }}
      />

      <Snackbar
        open={Boolean(actions.notice)}
        autoHideDuration={6000}
        onClose={actions.clearMessages}
        message={actions.notice}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};
